import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import { mvkgTokens } from '../theme/mvkgTokens';

/**
 * Keyboard shortcuts overlay — UX audit 2026-05-26 §1.
 *
 * Press `?` (Shift + /) anywhere outside an input/textarea to open this. It
 * lists every globally-bound shortcut so users don't have to discover them by
 * accident. The previous state was "no `?` to show them" — this is the fix.
 *
 * Shortcut definitions live in this file (single source) and the global
 * keyboard listeners that own each shortcut should keep their own canonical
 * binding; we just describe them here.
 */
const SHORTCUTS = [
  { keys: ['Ctrl', 'K'], aka: ['⌘', 'K'], desc: 'Open command palette / global search' },
  { keys: ['?'], desc: 'Show this shortcut overlay' },
  { keys: ['Esc'], desc: 'Close any open dialog or right panel' },
  { keys: ['/'], desc: 'Focus the in-page search field (when present)' },
  // Sigma graph shortcuts — only active on /graph; documented so users know
  // they exist.
  { keys: ['+', '−'], desc: 'Zoom in / out (graph view)' },
  { keys: ['F'], desc: 'Fit graph to viewport' },
  { keys: ['Click empty canvas'], desc: 'Clear node/edge selection (graph view)' },
];

function isTypingInField(target) {
  if (!target) return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea') return true;
  if (target.isContentEditable) return true;
  return false;
}

function KeyChip({ children }) {
  return (
    <Box
      component="kbd"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 22,
        px: 0.75,
        borderRadius: 0.75,
        border: '1px solid rgba(0,0,0,0.18)',
        bgcolor: 'rgba(255,255,255,0.85)',
        color: 'text.primary',
        fontFamily: mvkgTokens.font.mono,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {children}
    </Box>
  );
}

export default function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      // `?` and Esc handling. We don't fire when the user is typing in a
      // field — pressing `?` in a search box should still type the character.
      if (e.key === '?' && !isTypingInField(e.target)) {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 3 } },
      }}
    >
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
          Keyboard
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
          Shortcuts
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1.25}>
          {SHORTCUTS.map((s, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.primary">
                {s.desc}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                {s.keys.map((k, j) => (
                  <KeyChip key={`m-${j}`}>{k}</KeyChip>
                ))}
                {s.aka && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
                      or
                    </Typography>
                    {s.aka.map((k, j) => (
                      <KeyChip key={`a-${j}`}>{k}</KeyChip>
                    ))}
                  </>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', mt: 2, fontFamily: mvkgTokens.font.mono }}
        >
          Press ? to toggle · Esc to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
